import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { map, switchMap, takeUntil, tap } from 'rxjs/operators';

import { injectCatchHttpError } from '../../../../core/errors/catch-http-error.util';
import { SnackBarType } from '../../../../core/models/snack-bar.models';
import { PaginationService } from '../../../../core/services/pagination/pagination.service';
import { SnackBarService } from '../../../../core/services/snack-bar/snack-bar.service';
import { ItemPaginationType } from '../../../../core/store/pagination/pagination.actions';
import { DetectUnsavedTableChangesService } from '../../table/services/table/detect-unsaved-table-changes.service';
import { FilteredTableApiService } from '../services/filtered-table-api.service';
import {
  cancelFilteredTableRequest,
  createFilteredTable,
  createFilteredTableFail,
  createFilteredTableSuccess,
  deleteFilteredTable,
  deleteFilteredTableFail,
  deleteFilteredTableSuccess,
  getFilteredTable,
  getFilteredTableFail,
  getFilteredTableSuccess,
  setFilteredTable,
  updateFilteredTable,
  updateFilteredTableFail,
  updateFilteredTableSuccess,
} from './filtered-table.actions';

@Injectable()
export class FilteredTableEffects {
  private filteredTableApi = inject(FilteredTableApiService);
  private paginationService = inject(PaginationService);
  private detectUnsavedTableChangesService = inject(DetectUnsavedTableChangesService);
  private snackBarService = inject(SnackBarService);

  private readonly actions$ = inject<Actions>(Actions);
  private readonly catchHttpError = injectCatchHttpError();

  createFilteredTable$ = createEffect(() =>
    this.actions$.pipe(
      ofType(createFilteredTable),
      map(action => action.params),
      switchMap(params =>
        this.filteredTableApi.createFilteredTable(params).pipe(
          switchMap(filteredTable => {
            this.paginationService.addItem(filteredTable, ItemPaginationType.FilteredTablesDropdown);
            this.snackBarService.show(SnackBarType.Success, 'FILTERED_TABLE.VIEW_CHANGED');
            return [createFilteredTableSuccess(), setFilteredTable({ filteredTable })];
          }),
          tap(() => {
            this.detectUnsavedTableChangesService.tableColumnsSaved$.emit();
          }),
          this.catchHttpError(() => createFilteredTableFail()),
        ),
      ),
    ),
  );

  updateFilteredTable$ = createEffect(() =>
    this.actions$.pipe(
      ofType(updateFilteredTable),
      switchMap(action =>
        this.filteredTableApi.updateFilteredTable(action.id, action.updateFilteredTableParams).pipe(
          switchMap(filteredTable => {
            this.paginationService.updateItem({ id: action.id, patch: filteredTable }, ItemPaginationType.FilteredTablesDropdown);
            this.snackBarService.show(SnackBarType.Success, 'FILTERED_TABLE.VIEW_CHANGED');
            return [updateFilteredTableSuccess(), setFilteredTable({ filteredTable })];
          }),
          tap(() => {
            this.detectUnsavedTableChangesService.tableColumnsSaved$.emit();
          }),
          this.catchHttpError(() => updateFilteredTableFail()),
        ),
      ),
    ),
  );

  deleteFilteredTable$ = createEffect(() =>
    this.actions$.pipe(
      ofType(deleteFilteredTable),
      switchMap(action =>
        this.filteredTableApi.deleteFilteredTable(action.id).pipe(
          switchMap(() => {
            this.paginationService.deleteItem({ id: action.id }, ItemPaginationType.FilteredTablesDropdown);
            return [deleteFilteredTableSuccess(), setFilteredTable({ filteredTable: undefined })];
          }),
          this.catchHttpError(() => deleteFilteredTableFail()),
        ),
      ),
    ),
  );

  getFilteredTable$ = createEffect(() =>
    this.actions$.pipe(
      ofType(getFilteredTable),
      switchMap(action =>
        this.filteredTableApi.getFilteredTable(action.id).pipe(
          takeUntil(this.actions$.pipe(ofType(cancelFilteredTableRequest))),
          switchMap(filteredTable => [getFilteredTableSuccess({ filteredTable })]),
          tap(() => {
            this.detectUnsavedTableChangesService.tableColumnsSaved$.emit();
          }),
          this.catchHttpError(() => getFilteredTableFail()),
        ),
      ),
    ),
  );
}
